/**
 * Leaderboard — named, sortable, every AE in the tenant.
 * Locked weights (May 5, 2026): WON 100 / COMMIT 90 / PROBABLE 80 / UPSIDE 60 / OMITTED 0
 * Score floors: 90 / 80 / 60. Weighted pipeline = sum(deal_value × bucket_weight).
 *
 * 4-color attainment (closed_ytd / plan_ytd):
 *   green ≥95% / yellow 80-94% / amber 60-79% / red <60%
 *
 * W23 fixes:
 *   - OperatorShell wrapper added (post-W12 standard)
 *   - SIM-% and SMOKE_TEST_% reps filtered client-side via isDemoData/isDemoEmail
 *   - Tie-breaking sort: primary key, secondary won_value DESC, tertiary full_name ASC
 *   - Attainment % shown alongside color badge
 *   - Mobile-safe horizontal scroll wrapper on table
 *   - Empty state copy polished
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/session";
import OperatorShell from "@/components/operator/OperatorShell";
import SprintBoyToggle from "@/components/SprintBoyToggle";
import DaysRemainingStrip from "@/components/DaysRemainingStrip";
import { useSprintView } from "@/lib/sprint-context";
import { isDemoData, isDemoEmail } from "@shared/demoDataFilter";

interface LeaderboardRow {
  ae_user_id: string;
  full_name: string;
  email: string;
  role: string;
  quota_annual: number | string;
  active_opps: number;
  commit_value: number | string;
  probable_value: number | string;
  upside_value: number | string;
  omitted_value: number | string;
  won_value: number | string;
  weighted_pipeline: number | string;
}

type SortKey =
  | "full_name"
  | "quota_annual"
  | "won_value"
  | "weighted_pipeline"
  | "commit_value"
  | "probable_value"
  | "upside_value"
  | "active_opps";

function fmt$(v: number | string): string {
  const n = Number(v) || 0;
  if (n === 0) return "\u2014";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtPct(closed: number, quota: number): string {
  if (quota <= 0) return "\u2014";
  return `${Math.round((closed / quota) * 100)}%`;
}

function attainmentColor(
  closed: number,
  quota: number
): { bg: string; text: string; label: string } {
  if (quota <= 0) return { bg: "#F3F4F6", text: "#6B7280", label: "No quota" };
  const pct = closed / quota;
  if (pct >= 0.95) return { bg: "#D1FAE5", text: "#065F46", label: "On track" };
  if (pct >= 0.8) return { bg: "#FEF3C7", text: "#92400E", label: "Watch" };
  if (pct >= 0.6) return { bg: "#FED7AA", text: "#9A3412", label: "At risk" };
  return { bg: "#FECACA", text: "#991B1B", label: "Off track" };
}

/**
 * Tie-breaking comparator.
 * Primary  : requested key (asc or desc)
 * Secondary: won_value DESC (closed beats pipeline on tie)
 * Tertiary : full_name ASC  (stable alpha)
 */
function compareRows(
  a: LeaderboardRow,
  b: LeaderboardRow,
  sortKey: SortKey,
  sortDir: "asc" | "desc"
): number {
  const isName = sortKey === "full_name";
  const av = isName ? a.full_name : Number(a[sortKey]) || 0;
  const bv = isName ? b.full_name : Number(b[sortKey]) || 0;

  if (av < bv) return sortDir === "asc" ? -1 : 1;
  if (av > bv) return sortDir === "asc" ? 1 : -1;

  // Secondary: won_value DESC
  if (sortKey !== "won_value") {
    const aw = Number(a.won_value) || 0;
    const bw = Number(b.won_value) || 0;
    if (bw !== aw) return bw - aw;
  }

  // Tertiary: name ASC
  return a.full_name.localeCompare(b.full_name);
}

export default function Leaderboard() {
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("weighted_pipeline");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const { sprintView } = useSprintView();

  useEffect(() => {
    (async () => {
      try {
        const data = await api<LeaderboardRow[]>("/api/leaderboard");
        setRows(data);
      } catch (e: any) {
        setError(e?.message ?? "Failed to load leaderboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter out SIM-% and SMOKE_TEST_% reps (preventive client-side guard)
  const clean = rows.filter(
    (r) => !isDemoData(r.full_name) && !isDemoEmail(r.email)
  );

  // Role filter: ae + manager only
  const aes = clean.filter((r) => r.role === "ae" || r.role === "manager");

  // Sort with tie-breaking
  const sorted = [...aes].sort((a, b) => compareRows(a, b, sortKey, sortDir));

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir(k === "full_name" ? "asc" : "desc");
    }
  };

  const arrow = (k: SortKey) => {
    if (k !== sortKey) return "";
    return sortDir === "asc" ? " \u2191" : " \u2193";
  };

  const columns: { k: SortKey; label: string; align: "left" | "right" }[] = [
    { k: "full_name", label: "AE", align: "left" },
    { k: "quota_annual", label: "Annual quota", align: "right" },
    { k: "won_value", label: "Closed YTD", align: "right" },
    { k: "commit_value", label: "Commit", align: "right" },
    { k: "probable_value", label: "Upside", align: "right" },
    { k: "upside_value", label: "Best Case", align: "right" },
    { k: "weighted_pipeline", label: "Weighted forecast", align: "right" },
    { k: "active_opps", label: "Active opps", align: "right" },
  ];

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Executive", href: "/dashboards/executive" },
        { label: "Leaderboard" },
      ]}
      pageTitle="Leaderboard"
      pageMeta="Every AE side by side. Sortable. Click any column header to re-sort."
      showContextTabs={false}
    >
      {/* Header row: sprint toggle + forecast weight legend */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <SprintBoyToggle />
          {sprintView && (
            <p style={{ fontSize: 12, color: "#6B7280", margin: "6px 0 0 0" }}>
              Sprint view active — showing sprint-targeted accounts only.
            </p>
          )}
        </div>

        <div
          style={{
            fontSize: 12,
            color: "#6B7280",
            background: "#FFFFFF",
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid #E5E7EB",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ fontWeight: 600, color: "#111827", marginBottom: 4 }}>
            Forecast bucket weights
          </div>
          <div>
            WON 100%&nbsp;&nbsp;&middot;&nbsp;&nbsp;COMMIT 90%&nbsp;&nbsp;&middot;&nbsp;&nbsp;PROBABLE
            80%&nbsp;&nbsp;&middot;&nbsp;&nbsp;UPSIDE 60%&nbsp;&nbsp;&middot;&nbsp;&nbsp;OMITTED 0%
          </div>
        </div>
      </div>

      <DaysRemainingStrip />

      {/* Loading state */}
      {loading && (
        <div
          style={{
            padding: "40px 24px",
            color: "#6B7280",
            textAlign: "center",
            fontSize: 14,
          }}
        >
          Loading leaderboard&#8230;
        </div>
      )}

      {/* Error state */}
      {error && (
        <div
          style={{
            padding: 16,
            background: "#FEF2F2",
            color: "#991B1B",
            border: "1px solid #FECACA",
            borderRadius: 6,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Table with horizontal scroll guard for narrow viewports */}
      {!loading && !error && (
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 760,
            }}
          >
            <thead>
              <tr style={{ background: "#F9FAFB" }}>
                {columns.map((col) => (
                  <th
                    key={col.k}
                    onClick={() => toggleSort(col.k)}
                    style={{
                      textAlign: col.align,
                      padding: "10px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: col.k === sortKey ? "#111827" : "#374151",
                      cursor: "pointer",
                      userSelect: "none",
                      borderBottom: "1px solid #E5E7EB",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                    {arrow(col.k)}
                  </th>
                ))}
                {/* Attainment column — not user-sortable; computed inline */}
                <th
                  style={{
                    textAlign: "center",
                    padding: "10px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    whiteSpace: "nowrap",
                  }}
                >
                  Attainment
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Empty state */}
              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    style={{
                      padding: "48px 24px",
                      textAlign: "center",
                      color: "#6B7280",
                      fontSize: 14,
                      lineHeight: 1.6,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 8,
                      }}
                    >
                      No reps to display yet.
                    </div>
                    <div>
                      Closed deals and weighted pipeline appear once AEs have
                      active sales plans and accounts in motion. Set up plans in
                      Settings &#8594; Sales Plans.
                    </div>
                  </td>
                </tr>
              )}

              {/* Data rows */}
              {sorted.map((r) => {
                const closed = Number(r.won_value) || 0;
                const quota = Number(r.quota_annual) || 0;
                const c = attainmentColor(closed, quota);
                const pct = fmtPct(closed, quota);
                return (
                  <tr
                    key={r.ae_user_id}
                    style={{ borderBottom: "1px solid #F3F4F6" }}
                  >
                    {/* AE name + email + role badge for managers */}
                    <td style={{ padding: "12px", fontSize: 14, minWidth: 160 }}>
                      <div style={{ fontWeight: 600, color: "#111827" }}>
                        {r.full_name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#6B7280",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        {r.email}
                        {r.role === "manager" && (
                          <span
                            style={{
                              padding: "1px 6px",
                              background: "#F3F4F6",
                              color: "#374151",
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: "0.03em",
                              textTransform: "uppercase",
                            }}
                          >
                            Mgr
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Annual quota */}
                    <td
                      style={{
                        padding: "12px",
                        fontSize: 14,
                        textAlign: "right",
                        color: "#111827",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt$(r.quota_annual)}
                    </td>

                    {/* Closed YTD */}
                    <td
                      style={{
                        padding: "12px",
                        fontSize: 14,
                        textAlign: "right",
                        color: "#065F46",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt$(r.won_value)}
                    </td>

                    {/* Commit */}
                    <td
                      style={{
                        padding: "12px",
                        fontSize: 14,
                        textAlign: "right",
                        color: "#374151",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt$(r.commit_value)}
                    </td>

                    {/* Probable */}
                    <td
                      style={{
                        padding: "12px",
                        fontSize: 14,
                        textAlign: "right",
                        color: "#374151",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt$(r.probable_value)}
                    </td>

                    {/* Upside */}
                    <td
                      style={{
                        padding: "12px",
                        fontSize: 14,
                        textAlign: "right",
                        color: "#374151",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt$(r.upside_value)}
                    </td>

                    {/* Weighted forecast */}
                    <td
                      style={{
                        padding: "12px",
                        fontSize: 14,
                        textAlign: "right",
                        color: "#111827",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt$(r.weighted_pipeline)}
                    </td>

                    {/* Active opps */}
                    <td
                      style={{
                        padding: "12px",
                        fontSize: 14,
                        textAlign: "right",
                        color: "#374151",
                      }}
                    >
                      {r.active_opps}
                    </td>

                    {/* Attainment badge + pct */}
                    <td
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 10px",
                          background: c.bg,
                          color: c.text,
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 999,
                        }}
                      >
                        {c.label}
                        {quota > 0 && (
                          <span style={{ fontWeight: 400, opacity: 0.85 }}>
                            {pct}
                          </span>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer note */}
      <div
        style={{
          marginTop: 16,
          fontSize: 11,
          color: "#9CA3AF",
          textAlign: "center",
        }}
      >
        Locked weights and color rules from May 5, 2026. Bucket weights are
        configurable per company at the manager level.
      </div>
    </OperatorShell>
  );
}
